import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const DATE_FORMAT = "dd 'de' MMMM 'de' yyyy";
const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

type MaterialSummary = {
	description?: string | null;
	brand?: string | null;
	model?: string | null;
	newStock?: number | null;
	usedStock?: number | null;
};

type RepairSummary = {
	repairNumber?: string | number | null;
};

type TechnicianSummary = {
	name?: string | null;
};

type DamagedMaterialEntry = {
	id?: string | number | null;
	material: MaterialSummary;
	repair?: RepairSummary | null;
	technician?: TechnicianSummary | null;
	createdAt?: Date | string | null;
	observation?: string | null;
};

type DamagedMaterialTableProps = {
	type: string;
	data: DamagedMaterialEntry[];
	loading?: boolean;
	onEvaluate: (type: string, entry: DamagedMaterialEntry) => void;
};

const formatMaterialName = (material: MaterialSummary) => {
	const description = material.description?.trim() ?? "";
	const brand = material.brand?.trim() ?? "";
	const model = material.model?.trim() ?? "";
	const parts = [description, brand, model].filter((value) => value.length > 0);

	return parts.join(", ") || "-";
};

const formatDate = (value?: Date | string | null) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;

	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, DATE_FORMAT);
};

export function DamagedMaterialTable({
	type,
	data,
	loading = false,
	onEvaluate,
}: DamagedMaterialTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`damaged-skeleton-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-40 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-20 rounded bg-muted/40" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={7}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem registos para analisar.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	const renderRows = () => (
		<TableBody>
			{data.map((entry) => {
				const materialName = formatMaterialName(entry.material);
				const newStock = entry.material.newStock ?? "-";
				const usedStock = entry.material.usedStock ?? "-";
				const repairNumber = entry.repair?.repairNumber ?? "-";
				const technicianName = entry.technician?.name ?? "-";
				const rowKey = entry.id ?? `${type}-${materialName}-${repairNumber}`;

				return (
					<TableRow key={rowKey}>
						<TableCell>{materialName}</TableCell>
						<TableCell>{`${newStock} / ${usedStock}`}</TableCell>
						<TableCell>{repairNumber}</TableCell>
						<TableCell>{technicianName}</TableCell>
						<TableCell>{formatDate(entry.createdAt)}</TableCell>
						<TableCell>{entry.observation || "-"}</TableCell>
						<TableCell className="text-right">
							<Button
								size="sm"
								onClick={() => onEvaluate(type, entry)}
								disabled={loading}
							>
								Avaliar
							</Button>
						</TableCell>
					</TableRow>
				);
			})}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[120px]">Stock N/U</TableHead>
						<TableHead className="w-[90px]">#</TableHead>
						<TableHead className="w-[150px]">Técnico</TableHead>
						<TableHead className="w-[180px]">Data</TableHead>
						<TableHead>Observação</TableHead>
						<TableHead className="w-[120px] text-right">Ações</TableHead>
					</TableRow>
				</TableHeader>
				{loading
					? renderSkeleton()
					: data.length > 0
						? renderRows()
						: renderEmpty()}
			</Table>
		</div>
	);
}
